import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';

// Leave room for Firestore's document name, field names, maps and timestamps.
// Rich editor HTML can contain entire pasted images, not just their URLs.
export const QUESTION_DOCUMENT_BUDGET = 700 * 1024;
const CONTENT_FIELDS = [
    'text', 'images', 'options', 'correctAnswer', 'prompts', 'rows', 'columns',
    'explanation', 'explanationText', 'suggestedAnswerContent',
    'suggestedAnswerExcel', 'markingSchemeContent',
];
const encoder = typeof TextEncoder === 'undefined' ? null : new TextEncoder();
const utf8Bytes = text => encoder ? encoder.encode(text).length : new Blob([text]).size;
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

// JSON length alone undercounts numeric arrays and nested spreadsheet maps.
export function questionDocumentBytes(value) {
    if (value == null || typeof value === 'boolean') return 1;
    if (typeof value === 'number' || value instanceof Date || typeof value?.toMillis === 'function') return 8;
    if (typeof value === 'string') return utf8Bytes(value) + 1;
    if (Array.isArray(value)) return 32 + value.reduce((sum, item) => sum + questionDocumentBytes(item), 0);
    return 32 + Object.entries(value).reduce((sum, [key, item]) => sum + utf8Bytes(key) + 1 + questionDocumentBytes(item), 0);
}

export async function prepareQuestionForSave(entry, { onStatus = () => {} } = {}) {
    // Explicitly clear the reference when a formerly large question becomes small.
    const document = { ...entry, contentStorage: null };
    if (questionDocumentBytes(document) <= QUESTION_DOCUMENT_BUDGET) return document;

    const content = {};
    CONTENT_FIELDS.forEach(field => {
        if (!Object.prototype.hasOwnProperty.call(document, field)) return;
        content[field] = document[field];
        // updateDoc merges fields: placeholders must replace old inline content.
        document[field] = Array.isArray(document[field]) ? [] : typeof document[field] === 'string' ? '' : null;
    });
    if (questionDocumentBytes(document) > QUESTION_DOCUMENT_BUDGET - 4096) {
        throw new Error('The question details are too large. Shorten the topic, title or search tags and save again.');
    }
    onStatus('Uploading question content… Please keep this page open.');
    const payload = new Blob([JSON.stringify({ version: 1, paperId: entry.paperId, content })], { type: 'application/json' });
    // Never overwrite a published file. If a later Firestore write fails, the
    // previously saved question still points to its complete, unchanged version.
    const version = Array.from(crypto.getRandomValues(new Uint32Array(4)), n => n.toString(16).padStart(8, '0')).join('');
    const path = `descriptive-assets/iq-content-${Date.now()}-${version}.json`;
    const uploaded = await uploadBytes(ref(storage, path), payload, { contentType: 'application/json', cacheControl: 'private, max-age=3600' });
    const url = await getDownloadURL(uploaded.ref);
    if (!url) throw new Error('The question upload did not return a download link. Please try saving again.');
    document.contentStorage = { version: 1, url, path, bytes: payload.size, fields: Object.keys(content) };
    if (questionDocumentBytes(document) > QUESTION_DOCUMENT_BUDGET) throw new Error('The question details are still too large. Shorten the topic or search tags.');
    return document;
}

export async function resolveQuestionContent(question) {
    const reference = question.contentStorage;
    if (reference == null) return question; // Existing inline questions need no migration.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
        if (reference.version !== 1 || typeof reference.url !== 'string' || !reference.url ||
            !Array.isArray(reference.fields) || !reference.fields.length ||
            reference.fields.some(field => !CONTENT_FIELDS.includes(field))) throw new Error('Invalid content reference');
        const response = await fetch(reference.url, { signal: controller.signal });
        if (!response.ok) throw new Error('Content download failed');
        const payload = await response.json();
        if (payload?.version !== 1 || payload.paperId !== question.paperId || !isObject(payload.content) ||
            Object.keys(payload.content).length !== reference.fields.length ||
            reference.fields.some(field => !Object.prototype.hasOwnProperty.call(payload.content, field))) throw new Error('Invalid question content');
        // Only restore the declared content fields; identity, numbering, topic,
        // permissions and video metadata remain authoritative in Firestore.
        return { ...question, ...Object.fromEntries(reference.fields.map(field => [field, payload.content[field]])) };
    } catch {
        const error = new Error(`Could not load the full content for ${question.questionNumber ? `question ${question.questionNumber}` : 'a question'}. Check your connection and retry. If this continues, check access to the question file in Firebase Storage.`);
        error.code = 'iq/content-unavailable';
        throw error; // Never turn a download failure into an editable blank question.
    } finally {
        clearTimeout(timeout);
    }
}

export async function resolveQuestionContentBatch(questions, {allowUnavailable = false} = {}) {
    const resolved = new Array(questions.length);
    let next = 0;
    // Avoid flooding the connection when a paper has many large questions.
    await Promise.all(Array.from({ length: Math.min(4, questions.length) }, async () => {
        while (next < questions.length) {
            const index = next++;
            try {resolved[index] = await resolveQuestionContent(questions[index]);}
            catch(error) {if(!allowUnavailable)throw error;resolved[index]={...questions[index],contentUnavailable:true};}
        }
    }));
    return resolved;
}
